/// <amd-module name="Wit/Common/NodeHelpers" />
import { TreeNodeStructureType, WorkItemClassificationNode } from "Wit/Clients/Wit/Contracts/WorkItemTracking";

export const enum INodeStructureType {
    Project = 0,
    Area = 1,
    Iteration = 2
}

export interface INode {
    id: number;
    name: string;
    guid?: string;
    structure?: INodeStructureType;
    type?: number;
    children: INode[];
    parent?: INode;
    startDate?: Date;
    finishDate?: Date;
}

export function link(parentNode: INode): INode {
    if (parentNode.children) {
        for (const node of parentNode.children) {
            node.parent = parentNode;
            link(node);
        }
    }

    return parentNode;
}

export function findChildByStructure(node: INode, structure: number): INode | null {
    const children = node.children;

    if (children) {
        for (const child of children) {
            if (child.structure === structure) {
                return child;
            }
        }
    }

    return null;
}

export function findById(parentNode: INode, nodeId: number): INode | null {
    if (parentNode) {
        if (parentNode.id === nodeId) {
            return parentNode;
        }

        if (parentNode.children) {
            for (const child of parentNode.children) {
                const node = findById(child, nodeId);

                if (node) {
                    return node;
                }
            }
        }
    }

    return null;
}

export function findByGuid(parentNode: INode, nodeGuid: string): INode | null {
    if (parentNode) {
        if (parentNode.guid === nodeGuid) {
            return parentNode;
        }

        if (parentNode.children) {
            for (const child of parentNode.children) {
                const node = findByGuid(child, nodeGuid);

                if (node) {
                    return node;
                }
            }
        }
    }

    return null;
}

/** Finds a node by path in the given subtree, optionally handling an intermediate structure level ('Areas', 'Iterations', etc.).
 *  @param {string} parentNode     The node from which to start the search.
 *  @param {string} nodePath       The path to the desired node, EXCLUDING the structure node; e.g. "MyProject\MyArea" (not "MyProject\Areas\MyArea").
 *  @param {number} structureLevel The level at which the structure nodes exist, relative to the parent node (which is L0). Pass only if structure nodes exist
 *  @param {number} structureType  The type of structure node (Area, Iteration, etc.) to select at the structure level. Pass only if structure nodes exist
 *  @returns {INode} The node if found; null if not.
 */
export function findByPath(parentNode: INode, nodePath: string, structureLevel?: number, structureType?: number): INode | null {
    let i = 0;
    let j: number;
    let k: number;
    let level = 0; // Relative to parent node.
    let nodes: INode[] | null; // Nodes in this level to consider.
    let newNodes: INode[] | null; // Temporary holding for next level of nodes to consider.
    let foundNode: INode | null = null; // Latest found node.
    let pathSegments: string[];

    if (structureLevel != null && structureType == null) {
        throw new Error("ArgumentNullError: stuctureType must be provided if structureLevel is provided");
    }

    if (!parentNode || !nodePath) {
        return null;
    }

    // Normalize & split the path.
    nodePath = nodePath.replace(/^(\s|\u00A0|\\)+|(\s|\u00A0|\\)+$/g, ""); // Trim space & '\'.
    pathSegments = nodePath.toUpperCase().split("\\");

    // Starting with the parent node, traverse each level.
    // As long as we have nodes to consider and path segments to find...
    nodes = [parentNode];
    while (nodes && nodes.length > 0 && i < pathSegments.length) {
        foundNode = null;

        // (A) Special case: If a structure level was provided and if we've reached the structure level, find & enter the requested structure type.
        if (structureLevel != null && level++ === structureLevel) {
            newNodes = [];
            for (j = 0, k = nodes.length; j < k; j++) {
                if (nodes[j].structure === structureType) {
                    newNodes = nodes[j].children || [];
                    break;
                }
            }

            nodes = newNodes;
            continue;
        }

        // (B) Otherwise, find the next path segment node.
        newNodes = null;
        for (j = 0, k = nodes.length; j < k; j++) {
            if (nodes[j].name.toUpperCase() === pathSegments[i]) {
                foundNode = nodes[j];
                newNodes = foundNode.children;
                i++;
                break;
            }
        }

        nodes = newNodes;
    }

    // As long as we found all the segments, return the last found node.
    if (i === pathSegments.length) {
        return foundNode;
    } else {
        return null;
    }
}

export function getNodeSegments(node: INode | undefined, skipLevel?: number): string[] {
    /// <param name="skipLevel" type="number" optional="true" />

    const nodes: string[] = [];

    while (node) {
        nodes.unshift(node.name);
        node = node.parent;
    }

    if (typeof skipLevel === "number") {
        nodes.splice(skipLevel, 1);
    }

    return nodes;
}

/**
 * getNodesSegements for IReferencedNodes
 * @param path
 * @return the array of nodes values up to the root of the current node.
 */
export function getReferencedNodeSegmenets(path: string): string[] {
    const segments = path.split("\\");
    segments.unshift(segments[0]);
    return segments;
}

export function getPath(node: INode, skipLevel: number): string {
    const nodes = getNodeSegments(node, skipLevel);

    return nodes.join("\\");
}

/**
 * Get a path relative to the root node, as the API expects it
 * ex. Project/Sprints/Sprint 133 => Sprints/Sprint 133
 * @param path The path to modify
 */
export function getRelativePath(path: string): string {
    return path.split("\\").slice(1).join("\\");
}

export function mapClassificationNodesToLegacyNode(node: WorkItemClassificationNode): INode {
    let startDate = node.attributes ? node.attributes["startDate"] : null;
    let finishDate = node.attributes ? node.attributes["finishDate"] : null;

    startDate = typeof startDate === "string" ? new Date(startDate) : startDate;
    finishDate = typeof finishDate === "string" ? new Date(finishDate) : finishDate;

    const legacyNode: INode = {
        id: node.id,
        startDate: startDate,
        children: [],
        finishDate: finishDate,
        guid: node.identifier,
        name: node.name
    };

    if (node.structureType === TreeNodeStructureType.Area) {
        legacyNode.structure = INodeStructureType.Area;
        legacyNode.type = ClassificationNodeTypeConstants.AreaType;
    } else if (node.structureType === TreeNodeStructureType.Iteration) {
        legacyNode.structure = INodeStructureType.Iteration;
        legacyNode.type = ClassificationNodeTypeConstants.IterationType;
    }

    if (node.children) {
        for (const childNode of node.children) {
            legacyNode.children.push(mapClassificationNodesToLegacyNode(childNode));
        }
    }

    return legacyNode;
}

export function isClassificationPathEqualOrUnderRelative(
    pathToCheck: string,
    basePath: string,
    includeChildren: boolean,
    projectNames?: string[]
): boolean {
    const isValid = isEqualOrUnder(pathToCheck, basePath, includeChildren);
    return (
        isValid || !!projectNames?.some(projectName => isEqualOrUnder(pathToCheck, replaceClassificationRoot(basePath, projectName), includeChildren))
    );
}

function isEqualOrUnder(pathToCheck: string, basePath: string, includeChildren: boolean) {
    let isValid = basePath === pathToCheck;
    const classificationPart = basePath + "\\";

    if (!isValid && includeChildren && pathToCheck) {
        isValid = pathToCheck.startsWith(classificationPart);
    }

    return isValid;
}

function replaceClassificationRoot(path: string, newRoot: string): string {
    return path.replace(/^[^\\]+\s*/, newRoot);
}

export enum ClassificationNodeTypeConstants {
    ProjectType = -42,
    AreaType = -43,
    IterationType = -44
}
